import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Play, Volume2, VolumeX } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { VIDEO } from "@/data/content";
import { useYouTube } from "@/context/YouTubeContext";
import { scrollToId } from "@/components/SmoothScroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { latestUrl } = useYouTube();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [src, setSrc] = useState(null);

  // Pick the right encode once on the client; honour reduced-motion by showing the poster only.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setSrc(window.matchMedia("(max-width: 768px)").matches ? VIDEO.introMobile : VIDEO.intro);
  }, []);

  // React does not reflect `muted` as a DOM attribute, so set it explicitly before autoplaying.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !src) return;
    el.muted = true;
    el.play().catch(() => {});
  }, [src]);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    if (el.paused) el.play().catch(() => {});
  };

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <video
          ref={videoRef}
          key={src || "poster"}
          src={src || undefined}
          poster={VIDEO.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="hero-video"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 md:px-8 md:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          data-testid="hero-eyebrow"
          className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-ember sm:text-xs"
        >
          <span className="h-px w-10 bg-ember" aria-hidden="true" />
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="mt-8 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-cream sm:text-7xl lg:text-8xl" data-testid="hero-headline">
          <MaskedLine delay={0.35}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="font-editorial font-medium italic text-ember">{t.hero.line2}</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          data-testid="hero-description"
          className="mt-8 max-w-md text-base leading-relaxed text-ash md:text-lg"
        >
          {t.hero.description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={latestUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-primary-cta"
            className="group flex items-center gap-3 rounded-full bg-ember px-7 py-3.5 text-sm font-bold uppercase tracking-wider text-ink transition-colors duration-300 hover:bg-emberHover"
          >
            <Play className="h-4 w-4 fill-current" />
            {t.hero.primaryCta}
          </a>
          <button
            type="button"
            onClick={() => scrollToId("stories")}
            data-testid="hero-secondary-cta"
            className="group flex items-center gap-2 rounded-full border border-cream/25 px-7 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:border-ember hover:text-ember"
          >
            {t.hero.secondaryCta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>

      {src && (
        <motion.button
          type="button"
          onClick={toggleSound}
          data-testid="hero-sound-toggle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          aria-label={muted ? t.hero.unmute : t.hero.mute}
          aria-pressed={!muted}
          className="absolute bottom-8 left-5 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-cream/25 bg-ink/50 text-cream backdrop-blur transition-colors duration-300 hover:border-ember hover:text-ember md:bottom-10 md:left-8"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </motion.button>
      )}

      <motion.button
        type="button"
        onClick={() => scrollToId("interviews")}
        data-testid="hero-scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 right-6 hidden flex-col items-center gap-2 text-ash transition-colors duration-300 hover:text-ember md:flex md:right-10"
        aria-label={t.hero.scrollHint}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">{t.hero.scrollHint}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};
